import React from 'react';
import { useDispatch } from 'react-redux';
import {
  Box, Paper, Typography, TextField, ToggleButton, ToggleButtonGroup, Button, Pagination,
} from '@mui/material';
import {
  CheckCircleRounded as TabDoneIcon,
  ErrorOutline as SetupIcon,
  PendingActionsRounded as TabPendingIcon,
  TodayRounded as TabTodayIcon,
  ReplayRounded as TabExternalIcon,
} from '@mui/icons-material';
import * as actions from '../../redux/actions';
import { useBankAccounts } from './hooks/useBankAccounts';
import UnverifiedExpenseDatesCard from './components/cards/UnverifiedExpenseDatesCard';

// แถบแท็บกรองรายการวัน — ย้ายมาจากในการ์ด ให้ลอยอยู่นอกการ์ด บนสุดของหน้า (ตามที่ผู้ใช้ขอ)
// สไตล์ pill เดียวกับหน้า /finance/expenses (ExpensesList.js TABS): กล่องคลิกได้ ไอคอน + label +
// ตัวนับ, อันที่เลือกพื้นหลัง gradient ตัวอักษรขาว ยกขึ้นเล็กน้อย — สี gradient หยิบจากแท็บที่
// ความหมายตรงกันในหน้านั้น (วันนี้ = ม่วง, ค้าง = ส้ม, สำเร็จ = เขียว)
const TAB_DEFS = [
  { key: 'today', label: 'วันนี้', icon: TabTodayIcon, gradient: 'linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%)' },
  { key: 'pending', label: 'ค้าง', icon: TabPendingIcon, gradient: 'linear-gradient(135deg, #f59e0b 0%, #f97316 100%)' },
  { key: 'external', label: 'รอยืนยันอีกครั้ง', icon: TabExternalIcon, gradient: 'linear-gradient(135deg, #ef4444 0%, #f97316 100%)' },
  { key: 'verified', label: 'สำเร็จแล้ว', icon: TabDoneIcon, gradient: 'linear-gradient(135deg, #10b981 0%, #14b8a6 100%)' },
];

const DATE_PAGE_SIZE = 25;

const TAB_EMPTY_LABEL = {
  today: 'ไม่มีรายการของวันนี้',
  pending: 'ไม่มีรายการค้างตรวจสอบ',
  external: 'ไม่มีรายการรอยืนยันอีกครั้ง',
  verified: 'ยังไม่มีวันที่ยืนยันแล้ว',
};

const tabBarSx = {
  display: 'flex',
  flexWrap: 'wrap',
  justifyContent: 'center',
  gap: 0.75,
  p: 0.75,
  borderRadius: '18px',
  border: '1px solid',
  borderColor: 'divider',
  bgcolor: 'background.paper',
  boxShadow: '0 4px 20px rgba(15,23,42,0.06)',
};

const getTabItemSx = (selected, gradient) => ({
  display: 'flex',
  alignItems: 'center',
  gap: 0.9,
  px: 2,
  py: 1,
  borderRadius: '14px',
  cursor: 'pointer',
  userSelect: 'none',
  whiteSpace: 'nowrap',
  transition: 'transform 0.2s ease, background-color 0.2s ease, box-shadow 0.25s ease',
  transform: selected ? 'translateY(-1px)' : 'none',
  background: selected ? gradient : 'transparent',
  boxShadow: selected ? '0 6px 16px -4px rgba(0,0,0,0.35)' : 'none',
  '&:hover': { background: selected ? gradient : 'rgba(15,23,42,0.04)' },
});

const getTabBadgeSx = (selected) => ({
  minWidth: 20,
  height: 20,
  px: 0.7,
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  borderRadius: '999px',
  fontSize: '0.7rem',
  fontWeight: 700,
  lineHeight: 1,
  bgcolor: selected ? 'rgba(255,255,255,0.28)' : 'rgba(15,23,42,0.07)',
  color: selected ? '#fff' : 'text.secondary',
});

function SetupState({ title, description }) {
  return (
    <Paper variant="outlined" sx={{ mt: 3, p: { xs: 2.5, md: 4 }, borderRadius: 3, borderStyle: 'dashed', textAlign: 'center', bgcolor: '#fafafa' }}>
      <Box sx={{ mx: 'auto', mb: 1.5, width: 52, height: 52, borderRadius: '50%', display: 'grid', placeItems: 'center', bgcolor: '#fff7ed', color: '#ea580c' }}>
        <SetupIcon />
      </Box>
      <Typography variant="h6" sx={{ fontWeight: 700, mb: 0.75 }}>{title}</Typography>
      <Typography variant="body2" color="text.secondary" sx={{ maxWidth: 560, mx: 'auto' }}>{description}</Typography>
    </Paper>
  );
}

export function OcrDailyVerification() {
  const dispatch = useDispatch();
  const { bankAccounts } = useBankAccounts();
  const mainAccount = bankAccounts[0];
  const mainCustomerId = mainAccount?.customer_id || '';
  const companyOpeningBalance = Number(mainAccount?.company_opening_balance || 0);
  const companyOpeningBalanceDate = mainAccount?.company_opening_balance_date || null;

  const [rows, setRows] = React.useState([]);
  const [loading, setLoading] = React.useState(false);
  const [error, setError] = React.useState('');
  const [tab, setTab] = React.useState('pending');
  // ตัวกรองวันที่ของแท็บ "สำเร็จแล้ว" — 'all' | 'single' | 'range'
  const [dateMode, setDateMode] = React.useState('all');
  const [fDate, setFDate] = React.useState('');
  const [fFrom, setFFrom] = React.useState('');
  const [fTo, setFTo] = React.useState('');
  const [incomeAfterTotal, setIncomeAfterTotal] = React.useState(0);
  const [expenseAfterTotal, setExpenseAfterTotal] = React.useState(0);

  // fetch รายการวันที่รอตรวจสอบ — ย้ายมาจากในการ์ด (เดิมการ์ด fetch เองแล้วยิง callback บอกยอด
  // unverified กลับขึ้นมา) ตอนนี้ parent เป็นเจ้าของ rows คนเดียว: ใช้ทั้งกรองตามแท็บ, นับ badge,
  // และคำนวณ companyBalanceOcrVerified — เรียกซ้ำหลังยืนยัน OCR สำเร็จผ่าน onReload
  const reloadDates = React.useCallback(async () => {
    if (!mainCustomerId || !companyOpeningBalanceDate) return;
    setLoading(true);
    setError('');
    try {
      const data = await dispatch(actions.listUnverifiedExpenseDates({
        customerId: mainCustomerId, dateFrom: companyOpeningBalanceDate,
      }));
      setRows(data.rows || []);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [dispatch, mainCustomerId, companyOpeningBalanceDate]);

  React.useEffect(() => { reloadDates(); }, [reloadDates]);

  React.useEffect(() => {
    if (!mainCustomerId || !companyOpeningBalanceDate) return;
    dispatch(actions.listCustomerCashFlowLedger({
      customerId: mainCustomerId, page: 1, size: 1, dateFrom: companyOpeningBalanceDate,
    })).then((data) => {
      setIncomeAfterTotal(Number(data.incomeAfterTotal || 0));
      setExpenseAfterTotal(Number(data.expenseAfterTotal || 0));
    }).catch(() => {});
  }, [dispatch, mainCustomerId, companyOpeningBalanceDate]);

  // ยอด "ยังไม่ยืนยัน" ทั้งสองทิศทาง derive จาก rows ตรงๆ (เดิมการ์ดยิง callback ขึ้นมา) — ใช้หัก
  // ออกจากยอดรวมตั้งแต่ cutoff เพื่อได้ "ยืนยันแล้วจริง" ที่เอาไปคิด companyBalanceOcrVerified
  // (ส่งต่อให้ OcrVerifyDialog ผ่านการ์ด -> DayExpensesModal เทียบกับยอดที่ OCR อ่านได้จากรูป)
  const expenseUnverifiedTotal = rows.reduce((sum, row) => sum + Number(row.unverifiedExpenseAmount || 0), 0);
  const incomeUnverifiedTotal = rows.reduce((sum, row) => sum + Number(row.unverifiedIncomeAmount || 0), 0);
  const verifiedIncomeTotal = incomeAfterTotal - incomeUnverifiedTotal;
  const verifiedExpenseTotal = expenseAfterTotal - expenseUnverifiedTotal;
  const companyBalanceOcrVerified = companyOpeningBalance + verifiedIncomeTotal - verifiedExpenseTotal;

  // "วันนี้" กรองตามวันที่ (timezone ไทยผ่าน en-CA locale ที่คืนรูปแบบ YYYY-MM-DD พอดี, ตรงกับ
  // dateKey ที่ backend คำนวณด้วย +07:00) ส่วนอีก 3 แท็บกรองตาม count ที่ backend ส่งมา — ทุกชุด
  // ซ้อนทับกันได้ (วันที่ยืนยันบางส่วน = อยู่ทั้งแท็บ "รอยืนยันอีกครั้ง" และ "สำเร็จแล้ว")
  const todayKey = new Date().toLocaleDateString('en-CA', { timeZone: 'Asia/Bangkok' });

  // กติกาแท็บ (แต่ละแถว = 1 วันที่ ซ้อนกันได้):
  //  ค้าง            = วันนั้นยังไม่เคยยืนยัน OCR เลย (ไม่มี payment ระบบ verified + ไม่มีรายการเงา)
  //                    -> พอ payment ใบใดใบหนึ่งของวันนั้นถูกยืนยัน วันนั้นหลุดจาก "ค้าง" ทันที
  //  รอยืนยันอีกครั้ง = วันนั้นเริ่มยืนยันแล้ว แต่ยังมีของค้าง (payment ระบบยัง unverified หรือรายการเงา pending)
  //  สำเร็จแล้ว        = วันนั้นมีรายการที่ยืนยันแล้ว >= 1 (payment ระบบ หรือรายการเงาที่ฝ่ายการเงินยืนยัน)
  const hasOcrActivity = (row) => Number(row.verifiedCount) > 0
    || Number(row.externalPendingCount) > 0
    || Number(row.externalConfirmedCount) > 0;
  const hasOutstanding = (row) => Number(row.unverifiedCount) > 0 || Number(row.externalPendingCount) > 0;

  // แท็บ "รอยืนยันอีกครั้ง": remap ให้คอลัมน์จำนวน/ยอด สื่อถึง "ของที่ยังค้าง" ของวันนั้น
  const toExternalRow = (row) => {
    const income = Number(row.unverifiedIncomeAmount || 0) + Number(row.externalPendingIncomeAmount || 0);
    const expense = Number(row.unverifiedExpenseAmount || 0) + Number(row.externalPendingExpenseAmount || 0);
    return {
      ...row,
      verified: false,
      count: Number(row.unverifiedCount || 0) + Number(row.externalPendingCount || 0),
      totalAmount: income + expense,
      unverifiedIncomeAmount: income,
      unverifiedExpenseAmount: expense,
    };
  };

  const rowsByTab = {
    today: rows.filter((row) => String(row.date).slice(0, 10) === todayKey),
    pending: rows.filter((row) => !hasOcrActivity(row)),
    external: rows.filter((row) => hasOcrActivity(row) && hasOutstanding(row)).map(toExternalRow),
    verified: rows
      .filter((row) => Number(row.verifiedCount) > 0 || Number(row.externalConfirmedCount) > 0)
      .map((row) => ({
        ...row,
        verified: true,
        count: Number(row.verifiedCount || 0) + Number(row.externalConfirmedCount || 0),
      })),
  };

  // ตัวกรองวันที่ (เฉพาะแท็บ "สำเร็จแล้ว") — row.date เป็น "YYYY-MM-DD" เทียบสตริงตรงๆ ได้
  const applyDateFilter = (list) => {
    if (dateMode === 'single') {
      return fDate ? list.filter((r) => String(r.date).slice(0, 10) === fDate) : list;
    }
    if (dateMode === 'range') {
      return list.filter((r) => {
        const d = String(r.date).slice(0, 10);
        return (!fFrom || d >= fFrom) && (!fTo || d <= fTo);
      });
    }
    return list;
  };
  const displayRows = tab === 'verified' ? applyDateFilter(rowsByTab.verified) : rowsByTab[tab];
  const verifiedFilterActive = tab === 'verified' && dateMode !== 'all';

  // pagination ฝั่งหน้าเว็บ — แท็บ "สำเร็จแล้ว" โตขึ้นเรื่อยๆ ตามจำนวนวันที่ยืนยันแล้ว
  const [page, setPage] = React.useState(1);
  React.useEffect(() => { setPage(1); }, [tab, dateMode, fDate, fFrom, fTo]);
  const pageCount = Math.max(1, Math.ceil(displayRows.length / DATE_PAGE_SIZE));
  const safePage = Math.min(page, pageCount);
  const pagedRows = displayRows.slice((safePage - 1) * DATE_PAGE_SIZE, safePage * DATE_PAGE_SIZE);

  return (
    <Box sx={{ maxWidth: 1440, mx: 'auto', pb: 4 }}>
      {!mainCustomerId ? (
        <SetupState title="ยังไม่พบบัญชีหลักสำหรับตรวจสอบ" description={'กรุณาไปที่หน้า “ตั้งค่าคู่ยืม-คืน” และกำหนดบัญชีหลักก่อนเริ่มยืนยันรายการ OCR'} />
      ) : !companyOpeningBalanceDate ? (
        <SetupState title="ยังไม่ได้กำหนดยอดตั้งต้น" description={'กรุณาไปที่หน้า “บัญชีบริษัท” แล้วเลือก “กำหนดค่าตั้งต้น” เพื่อระบุยอดและวันที่เริ่มตรวจสอบ'} />
      ) : (
        <Box sx={{ mt: 2.5 }}>
          {rows.length > 0 && (
            <Box sx={{ mb: 2, display: 'flex', justifyContent: 'center' }}>
              <Paper elevation={0} sx={tabBarSx}>
                {TAB_DEFS.map((t) => {
                  const selected = tab === t.key;
                  const Icon = t.icon;
                  return (
                    <Box key={t.key} onClick={() => setTab(t.key)} sx={getTabItemSx(selected, t.gradient)}>
                      <Icon sx={{ fontSize: 19, color: selected ? '#fff' : 'text.secondary' }} />
                      <Typography sx={{ fontSize: '0.85rem', fontWeight: selected ? 700 : 500, color: selected ? '#fff' : 'text.secondary' }}>
                        {t.label}
                      </Typography>
                      <Box sx={getTabBadgeSx(selected)}>{rowsByTab[t.key].length}</Box>
                    </Box>
                  );
                })}
              </Paper>
            </Box>
          )}

          {tab === 'verified' && rows.length > 0 && (
            <Box sx={{ mb: 1.5, display: 'flex', justifyContent: 'center', alignItems: 'center', flexWrap: 'wrap', gap: 1 }}>
              <ToggleButtonGroup
                size="small"
                exclusive
                value={dateMode}
                onChange={(e, v) => v && setDateMode(v)}
              >
                <ToggleButton value="all">ทั้งหมด</ToggleButton>
                <ToggleButton value="single">วันเดียว</ToggleButton>
                <ToggleButton value="range">ช่วงวันที่</ToggleButton>
              </ToggleButtonGroup>
              {dateMode === 'single' && (
                <TextField
                  size="small"
                  type="date"
                  value={fDate}
                  onChange={(e) => setFDate(e.target.value)}
                  InputLabelProps={{ shrink: true }}
                />
              )}
              {dateMode === 'range' && (
                <>
                  <TextField size="small" type="date" label="ตั้งแต่" value={fFrom} onChange={(e) => setFFrom(e.target.value)} InputLabelProps={{ shrink: true }} />
                  <TextField size="small" type="date" label="ถึง" value={fTo} onChange={(e) => setFTo(e.target.value)} InputLabelProps={{ shrink: true }} />
                </>
              )}
              {verifiedFilterActive && (
                <>
                  <Typography variant="caption" color="text.secondary">
                    แสดง {displayRows.length} จาก {rowsByTab.verified.length} วัน
                  </Typography>
                  <Button size="small" onClick={() => { setDateMode('all'); setFDate(''); setFFrom(''); setFTo(''); }}>
                    ล้าง
                  </Button>
                </>
              )}
            </Box>
          )}

          <UnverifiedExpenseDatesCard
            customerId={mainCustomerId}
            companyBalanceOcrVerified={companyBalanceOcrVerified}
            rows={pagedRows}
            tab={tab}
            loading={loading}
            error={error}
            isEmpty={!loading && rows.length === 0}
            emptyLabel={verifiedFilterActive ? 'ไม่มีวันที่ยืนยันแล้วในช่วงที่เลือก' : TAB_EMPTY_LABEL[tab]}
            onReload={reloadDates}
          />

          {displayRows.length > DATE_PAGE_SIZE && (
            <Box sx={{ mt: 1.5, display: 'flex', justifyContent: 'center', alignItems: 'center', gap: 1.5, flexWrap: 'wrap' }}>
              <Pagination
                size="small"
                count={pageCount}
                page={safePage}
                onChange={(e, v) => setPage(v)}
              />
              <Typography variant="caption" color="text.secondary">
                {(safePage - 1) * DATE_PAGE_SIZE + 1}–{Math.min(safePage * DATE_PAGE_SIZE, displayRows.length)} จาก {displayRows.length} วัน
              </Typography>
            </Box>
          )}
        </Box>
      )}
    </Box>
  );
}

export default OcrDailyVerification;
